import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertTriangle,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Send,
  Loader2,
  ArrowUpRight,
  DollarSign,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dispute status configs
const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700', icon: Eye },
  awaiting_response: { label: 'Awaiting Response', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  escalated: { label: 'Escalated', color: 'bg-purple-100 text-purple-700', icon: ArrowUpRight },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

// Dispute type labels
const TYPE_LABELS = {
  quality_issue: 'Quality Issue',
  late_delivery: 'Late Delivery',
  pricing_dispute: 'Pricing Dispute',
  communication: 'Communication',
  not_as_described: 'Not as Described',
  other: 'Other',
};

// Resolution types
const RESOLUTION_TYPES = [
  { id: 'full_refund', label: 'Full Refund' },
  { id: 'partial_refund', label: 'Partial Refund' },
  { id: 'redo_work', label: 'Redo Work' },
  { id: 'replacement', label: 'Replacement' },
  { id: 'no_action', label: 'No Action Required' },
  { id: 'mutual_agreement', label: 'Mutual Agreement' },
];

/**
 * AdminDisputesPanel Component
 * Admin panel for managing disputes
 */
const AdminDisputesPanel = () => {
  const { formatCurrency } = useCountry();
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('open');
  
  // Modal states
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  
  // Response form
  const [responseMessage, setResponseMessage] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  
  // Resolution form
  const [resolution, setResolution] = useState({
    type: '',
    notes: '',
    refund_amount: '',
  });

  useEffect(() => {
    fetchDisputes();
    fetchStats();
  }, [filter]);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/services/disputes${params}`);
      setDisputes(response.data.disputes || []);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services/disputes/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const viewDispute = async (disputeId) => {
    try {
      const response = await axios.get(`${API_URL}/api/services/disputes/${disputeId}`);
      setSelectedDispute(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to fetch dispute:', error);
    }
  };

  const updateStatus = async (disputeId, status) => {
    try {
      await axios.put(`${API_URL}/api/services/disputes/${disputeId}/status?status=${status}`);
      await fetchDisputes();
      await fetchStats();
      if (selectedDispute?.id === disputeId) {
        viewDispute(disputeId);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const sendResponse = async () => {
    if (!responseMessage.trim() || !selectedDispute) return;
    
    setSendingResponse(true);
    try {
      await axios.post(`${API_URL}/api/services/disputes/${selectedDispute.id}/respond`, {
        message: responseMessage,
        responder: 'admin',
        attachments: [],
      });
      setResponseMessage('');
      viewDispute(selectedDispute.id);
    } catch (error) {
      console.error('Failed to send response:', error);
    } finally {
      setSendingResponse(false);
    }
  };

  const resolveDispute = async () => {
    if (!resolution.type || !resolution.notes || !selectedDispute) return;
    
    try {
      await axios.post(`${API_URL}/api/services/disputes/${selectedDispute.id}/resolve`, {
        resolution_type: resolution.type,
        resolution_notes: resolution.notes,
        refund_amount: resolution.refund_amount ? parseFloat(resolution.refund_amount) : null,
        action_items: [],
      });
      
      setShowResolveModal(false);
      setResolution({ type: '', notes: '', refund_amount: '' });
      setShowDetailModal(false);
      await fetchDisputes();
      await fetchStats();
    } catch (error) {
      console.error('Failed to resolve dispute:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-disputes-panel">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(STATUS_CONFIG).slice(0, 5).map(([key, config]) => {
          const Icon = config.icon;
          const count = stats?.by_status?.[key] || 0;
          return (
            <Card 
              key={key}
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                filter === key ? 'ring-2 ring-purple-500' : ''
              }`}
              onClick={() => setFilter(key)}
            >
              <CardContent className="p-4 text-center">
                <Icon className={`h-6 w-6 mx-auto mb-2 ${config.color.replace('bg-', 'text-').replace('-100', '-600')}`} />
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-gray-500">{config.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Disputes</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="awaiting_response">Awaiting Response</SelectItem>
            <SelectItem value="escalated">Escalated</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={() => { fetchDisputes(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Disputes List */}
      <Card>
        <CardHeader>
          <CardTitle>Disputes</CardTitle>
          <CardDescription>
            {disputes.length} dispute{disputes.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : disputes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p>No disputes in this category</p>
            </div>
          ) : (
            <div className="space-y-4">
              {disputes.map((dispute) => {
                const status = STATUS_CONFIG[dispute.status] || STATUS_CONFIG.open;
                const Icon = status.icon;
                
                return (
                  <div 
                    key={dispute.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={status.color}>
                            <Icon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                          <Badge variant="outline">
                            {TYPE_LABELS[dispute.dispute_type] || dispute.dispute_type}
                          </Badge>
                        </div>
                        
                        <h3 className="font-semibold text-gray-900">{dispute.subject}</h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {dispute.description}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Customer: {dispute.customer_name}</span>
                          <span>•</span>
                          <span>{new Date(dispute.created_at).toLocaleDateString()}</span>
                          {dispute.responses?.length > 0 && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MessageSquare className="h-3 w-3" />
                                {dispute.responses.length} responses
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => viewDispute(dispute.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedDispute && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <DialogTitle>{selectedDispute.subject}</DialogTitle>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={STATUS_CONFIG[selectedDispute.status]?.color}>
                    {STATUS_CONFIG[selectedDispute.status]?.label}
                  </Badge>
                  <Badge variant="outline">
                    {TYPE_LABELS[selectedDispute.dispute_type]}
                  </Badge>
                </div>
              </DialogHeader>

              {/* Dispute Info */}
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Issue Description</h4>
                  <p className="text-sm text-gray-600">{selectedDispute.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-gray-500">Customer</p>
                    <p className="font-medium">{selectedDispute.customer_name}</p>
                    <p className="text-sm text-gray-600">{selectedDispute.customer_phone}</p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-gray-500">Provider</p>
                    <p className="font-medium">{selectedDispute.provider?.business_name || 'Unknown'}</p>
                  </div>
                </div>

                {/* Responses Thread */}
                {selectedDispute.responses?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Conversation</h4>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedDispute.responses.map((resp, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg ${
                            resp.responder === 'admin' 
                              ? 'bg-purple-50 ml-8' 
                              : resp.responder === 'provider'
                              ? 'bg-blue-50 mr-8'
                              : 'bg-gray-50 mr-8'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium capitalize">{resp.responder}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(resp.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm">{resp.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolution Info */}
                {selectedDispute.resolution && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">Resolution</h4>
                    <p className="text-sm text-green-700">
                      <strong>Type:</strong> {selectedDispute.resolution.type}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {selectedDispute.resolution.notes}
                    </p>
                    {selectedDispute.resolution.refund_amount && (
                      <p className="text-sm text-green-700 mt-1">
                        <strong>Refund:</strong> {formatCurrency(selectedDispute.resolution.refund_amount)}
                      </p>
                    )}
                  </div>
                )}

                {/* Admin Response */}
                {selectedDispute.status !== 'resolved' && selectedDispute.status !== 'closed' && (
                  <div>
                    <h4 className="font-medium mb-2">Send Response</h4>
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your response..."
                        value={responseMessage}
                        onChange={(e) => setResponseMessage(e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      <Button 
                        onClick={sendResponse}
                        disabled={sendingResponse || !responseMessage.trim()}
                      >
                        {sendingResponse ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedDispute.status !== 'resolved' && selectedDispute.status !== 'closed' && (
                <DialogFooter className="flex justify-between mt-4">
                  <div className="flex gap-2">
                    {selectedDispute.status === 'open' && (
                      <Button 
                        variant="outline"
                        onClick={() => updateStatus(selectedDispute.id, 'under_review')}
                      >
                        Mark Under Review
                      </Button>
                    )}
                    {selectedDispute.status !== 'escalated' && (
                      <Button 
                        variant="outline"
                        className="text-purple-600 border-purple-300"
                        onClick={() => updateStatus(selectedDispute.id, 'escalated')}
                      >
                        <ArrowUpRight className="h-4 w-4 mr-1" />
                        Escalate
                      </Button>
                    )}
                  </div>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => setShowResolveModal(true)}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Resolve Dispute
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Resolve Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Dispute</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Resolution Type *</Label>
              <Select 
                value={resolution.type}
                onValueChange={(val) => setResolution({ ...resolution, type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select resolution type" />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_TYPES.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(resolution.type === 'full_refund' || resolution.type === 'partial_refund') && (
              <div>
                <Label>Refund Amount (Rs)</Label>
                <Input
                  type="number"
                  value={resolution.refund_amount}
                  onChange={(e) => setResolution({ ...resolution, refund_amount: e.target.value })}
                  placeholder="Enter amount"
                />
              </div>
            )}
            
            <div>
              <Label>Resolution Notes *</Label>
              <Textarea
                value={resolution.notes}
                onChange={(e) => setResolution({ ...resolution, notes: e.target.value })}
                placeholder="Explain the resolution..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={resolveDispute}
              disabled={!resolution.type || !resolution.notes}
            >
              Confirm Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDisputesPanel;
